import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  batchActorIdsForResolve,
  subscribeEntityAuditChanged,
} from "@playbookai/api-clients";
import type {
  PlayerAIMentionRoster,
  PlayerAIWidgetContext,
} from "@playbookai/api-clients";
import type { PlayerAIPersonMentionSource } from "@playbookai/ui";

export type MentionSearchResource = NonNullable<
  PlayerAIWidgetContext["peopleSearchResource"]
>;
export interface MentionablePerson {
  readonly id: string;
  readonly label: string;
}
const NO_PEOPLE: readonly MentionablePerson[] = [];

/** Complete membership pages, then resolve only their IDs (never a directory search). */
export async function loadMentionablePeople(input: {
  readonly roster: PlayerAIMentionRoster;
  readonly viewerId: string;
  readonly signal: AbortSignal;
}): Promise<readonly MentionablePerson[]> {
  const ids = new Set<string>();
  if (input.roster.ownerId) ids.add(input.roster.ownerId);
  let cursor: string | null = null;
  const seenCursors = new Set<string>();
  do {
    input.signal.throwIfAborted();
    const page = await input.roster.pageMembers(cursor, input.signal);
    for (const member of page.items) ids.add(member.userId);
    cursor = page.nextCursor;
    if (cursor !== null) {
      if (seenCursors.has(cursor))
        throw new Error("Member pagination did not advance");
      seenCursors.add(cursor);
    }
  } while (cursor !== null);
  ids.delete(input.viewerId);
  const identities = (
    await Promise.all(
      batchActorIdsForResolve([...ids]).map((batch) =>
        input.roster.resolveIdentities(batch, input.signal),
      ),
    )
  ).flat();
  input.signal.throwIfAborted();
  const names = new Map<string, string>();
  for (const identity of identities) {
    const label = identity.displayName?.trim();
    if (ids.has(identity.userId) && label && label !== identity.userId)
      names.set(identity.userId, label);
  }
  if ([...ids].some((id) => !names.has(id)))
    throw new Error("Some member names could not be loaded");
  return [...names]
    .map(([id, label]) => ({ id, label }))
    .sort(
      (left, right) =>
        left.label.localeCompare(right.label) ||
        left.id.localeCompare(right.id),
    );
}

/** One session/workspace-scoped roster cache. Typing only filters in the shared UI. */
export function usePersonMentionSearch(input: {
  readonly resource: MentionSearchResource | undefined;
  readonly viewerId: string | null;
  readonly tenantId: string | null;
  readonly authSessionGeneration: string | null;
  readonly enabled: boolean;
}): PlayerAIPersonMentionSource | undefined {
  const client = useQueryClient();
  const resource = input.resource;
  const active =
    input.enabled &&
    !!resource &&
    !!input.viewerId &&
    !!input.tenantId &&
    !!input.authSessionGeneration;
  const key = useMemo(
    () => [
      "resource-access",
      input.tenantId,
      input.viewerId,
      input.authSessionGeneration,
      resource?.resourceType === "hoshin"
        ? "hoshin_plan"
        : resource?.resourceType,
      resource?.resourceId,
      resource?.resourceType === "hoshin" ? "share" : "members",
      "player-ai-mention-roster",
      resource?.roster.ownerId ?? null,
    ],
    [
      input.tenantId,
      input.viewerId,
      input.authSessionGeneration,
      resource?.resourceType,
      resource?.resourceId,
      resource?.roster.ownerId,
    ],
  );
  const stamp = JSON.stringify(key);
  const [openFor, setOpenFor] = useState<string | null>(null);
  const open = openFor === stamp;
  const search = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => {
      if (!resource || !input.viewerId)
        throw new Error("Mention scope unavailable");
      return loadMentionablePeople({
        roster: resource.roster,
        viewerId: input.viewerId,
        signal,
      });
    },
    enabled: active && open,
    staleTime: 30_000,
    retry: false,
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
  });
  useEffect(() => {
    if (!active) return;
    return subscribeEntityAuditChanged(() => {
      void client.invalidateQueries(
        { queryKey: key },
        { cancelRefetch: false },
      );
    });
  }, [active, client, key]);
  const onQueryChange = useCallback(
    (query: string | null) => {
      setOpenFor(query === null ? null : stamp);
    },
    [stamp],
  );
  const onRetry = useCallback(() => {
    void search.refetch();
  }, [search.refetch]);
  // Never present a failed or refreshing roster as a complete/current list.
  const options =
    open && !search.isFetching && !search.isError
      ? (search.data ?? NO_PEOPLE)
      : NO_PEOPLE;
  const status = search.isError
    ? "error"
    : search.isFetching || !search.data
      ? "loading"
      : "ready";
  return useMemo(
    () => (active ? { options, status, onQueryChange, onRetry } : undefined),
    [active, options, status, onQueryChange, onRetry],
  );
}
